import {
  AppleCalendar,
  Calendar,
  CalendarEvent,
  NativeAppleEvent,
} from '@demind-inc/core';
import dayjs, { Dayjs } from 'dayjs';

export interface AppleCalendarsBridgeResponse {
  ok: boolean;
  error?: string;
  authorizationStatus?: string;
  startDate?: string;
  endDate?: string;
  appleCalendars?: AppleCalendar[];
}

export interface AppleEventMutationBridgeResponse {
  ok: boolean;
  error?: string;
  authorizationStatus?: string;
  event?: NativeAppleEvent | null;
}

export interface AppleEventDeleteBridgeResponse {
  ok: boolean;
  error?: string;
  authorizationStatus?: string;
  deleted?: boolean;
}

export interface AppleDesktopRecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek?: Array<{ day: string; weekNumber?: number }>;
}

export interface CreateAppleDesktopEventData {
  title: string;
  startDate: string;
  endDate: string;
  description?: string;
  isAllDay?: boolean;
  location?: string;
  recurrenceString?: string | null;
}

interface AppleCalendarBridgeSyncWindow {
  startTime: string;
  endTime: string;
}

interface DesktopBridgePayload {
  startDate?: string;
  endDate?: string;
}

interface DesktopCreateAppleEventPayload {
  title?: string;
  startDate?: string;
  endDate?: string;
  allDay?: boolean;
  description?: string;
  notes?: string;
  location?: string;
  url?: string;
  timeZone?: string;
  calendarIdentifier?: string;
  recurrenceRule?: AppleDesktopRecurrenceRule | null;
}

interface DesktopUpdateAppleEventPayload extends DesktopCreateAppleEventPayload {
  eventId: string;
  occurrenceDate?: string;
  originalOccurrenceDate?: string;
  futureEvents?: boolean;
  clearRecurrence?: boolean;
}

interface DesktopDeleteAppleEventPayload {
  eventId: string;
  occurrenceDate?: string;
  futureEvents?: boolean;
}

type RecurringEventSpan = 'thisEvent' | 'allEvents';

interface DesktopBridge {
  platform?: string;
  getAppleCalendars?: (payload?: DesktopBridgePayload) => Promise<AppleCalendarsBridgeResponse>;
  createAppleCalendarEvent?: (
    payload: DesktopCreateAppleEventPayload
  ) => Promise<AppleEventMutationBridgeResponse>;
  updateAppleCalendarEvent?: (
    payload: DesktopUpdateAppleEventPayload
  ) => Promise<AppleEventMutationBridgeResponse>;
  deleteAppleCalendarEvent?: (
    payload: DesktopDeleteAppleEventPayload
  ) => Promise<AppleEventDeleteBridgeResponse>;
  openAppleCalendarSettings?: () => Promise<boolean>;
}

const LOOK_BEHIND_DAYS = 1;
const LOOK_AHEAD_DAYS = 30;

const TEXT_TO_WEEKDAY_CODE: Record<string, string> = {
  sun: 'SU',
  mon: 'MO',
  tue: 'TU',
  wed: 'WE',
  thu: 'TH',
  fri: 'FR',
  sat: 'SA',
  sunday: 'SU',
  monday: 'MO',
  tuesday: 'TU',
  wednesday: 'WE',
  thursday: 'TH',
  friday: 'FR',
  saturday: 'SA',
};

const getDesktopBridge = (): DesktopBridge | undefined => {
  return (window as Window & { lifestack?: DesktopBridge }).lifestack;
};

const isBridgeHandlerMissing = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('no handler registered') ||
    normalized.includes('bridge unavailable') ||
    normalized.includes('undefined is not a function')
  );
};

const normalizeAppleBridgeError = (error: unknown): Error => {
  const rawMessage = error instanceof Error ? error.message : String(error);

  if (
    rawMessage.includes('calendar_permission_denied') ||
    rawMessage.includes('calendar_permission_write_only')
  ) {
    return new Error(
      'Apple Calendar permission denied. Enable Calendar access in macOS Settings and try again.'
    );
  }

  if (rawMessage.includes('eventkit_bridge_missing')) {
    return new Error(
      'Apple Calendar native bridge is missing. Run `yarn --cwd desktop native:build:eventkit`.'
    );
  }

  if (isBridgeHandlerMissing(rawMessage)) {
    return new Error('Apple Calendar desktop bridge is unavailable. Restart the desktop app.');
  }

  return new Error(rawMessage || 'Apple Calendar desktop bridge request failed.');
};

const ensureBridgeMethod = <T extends keyof DesktopBridge>(
  methodName: T
): NonNullable<DesktopBridge[T]> => {
  const desktopBridge = getDesktopBridge();
  const bridgeMethod = desktopBridge?.[methodName];

  if (desktopBridge?.platform !== 'darwin' || typeof bridgeMethod !== 'function') {
    throw new Error('Apple Calendar bridge unavailable in this environment.');
  }

  return bridgeMethod as NonNullable<DesktopBridge[T]>;
};

const parseAppleRawJson = (rawJson: unknown): Record<string, unknown> | undefined => {
  if (!rawJson) {
    return undefined;
  }

  if (typeof rawJson === 'string') {
    try {
      const parsed = JSON.parse(rawJson);
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  if (typeof rawJson === 'object') {
    return rawJson as Record<string, unknown>;
  }

  return undefined;
};

const getBooleanField = (
  source: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  if (!source) {
    return undefined;
  }

  const value = source[key];
  return typeof value === 'boolean' ? value : undefined;
};

const getRecurrenceString = (event: Partial<CalendarEvent>): string | null | undefined => {
  if (!Object.prototype.hasOwnProperty.call(event, 'recurrence')) {
    return undefined;
  }

  const recurrence = (event as Partial<CalendarEvent> & { recurrence?: unknown }).recurrence;
  if (!recurrence) {
    return null;
  }

  if (typeof recurrence === 'string') {
    return recurrence;
  }

  if (
    typeof recurrence === 'object' &&
    recurrence !== null &&
    'string' in recurrence &&
    typeof (recurrence as { string?: unknown }).string === 'string'
  ) {
    return (recurrence as { string: string }).string;
  }

  return null;
};

export const recurrenceStringToAppleRecurrenceRuleDesktop = (
  recurrenceString: string | undefined | null
): AppleDesktopRecurrenceRule | null => {
  if (!recurrenceString) {
    return null;
  }

  const lower = recurrenceString.trim().toLowerCase();

  if (lower === 'every day') {
    return { frequency: 'daily', interval: 1 };
  }

  if (lower === 'every weekday') {
    return {
      frequency: 'weekly',
      interval: 1,
      daysOfWeek: [
        { day: 'MO' },
        { day: 'TU' },
        { day: 'WE' },
        { day: 'TH' },
        { day: 'FR' },
      ],
    };
  }

  const dayMatch = lower.match(
    /^every\s+(sun|mon|tue|wed|thu|fri|sat|sunday|monday|tuesday|wednesday|thursday|friday|saturday)$/
  );
  if (dayMatch) {
    const dayCode = TEXT_TO_WEEKDAY_CODE[dayMatch[1]];
    if (dayCode) {
      return {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: [{ day: dayCode }],
      };
    }
  }

  const yearlyMatch = lower.match(
    /^every\s+(\d{1,2})(?:st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)$/
  );
  if (yearlyMatch) {
    return { frequency: 'yearly', interval: 1 };
  }

  const monthlyMatch = lower.match(/^every\s+(\d{1,2})(?:st|nd|rd|th)?$/);
  if (monthlyMatch) {
    return { frequency: 'monthly', interval: 1 };
  }

  return null;
};

export const transformEventForAppleCalendarDesktop = (
  event: Partial<CalendarEvent>
): CreateAppleDesktopEventData => {
  return {
    title: event.summary || '',
    startDate: dayjs(event.start?.date).toISOString(),
    endDate: dayjs(event.end?.date).toISOString(),
    description: event.description || '',
    location: event.location || '',
    isAllDay: event.isAllDay || false,
    recurrenceString: getRecurrenceString(event),
  };
};

export const extractNativeAppleEventId = (event?: Partial<CalendarEvent>): string | undefined => {
  if (!event) {
    return undefined;
  }

  const rawJson = parseAppleRawJson(event.rawJson);
  const rawEventId =
    rawJson?.id || rawJson?.eventId || rawJson?.calendarItemIdentifier || rawJson?.seriesId;

  if (typeof rawEventId === 'string' && rawEventId.trim()) {
    return rawEventId;
  }

  if (event.eventId && event.eventId.trim()) {
    return event.eventId;
  }

  return undefined;
};

export const extractAppleOccurrenceDate = (
  event?: Partial<CalendarEvent>
): string | undefined => {
  if (!event) {
    return undefined;
  }

  const rawJson = parseAppleRawJson(event.rawJson);
  const rawOccurrenceDate = rawJson?.occurrenceDate;

  if (typeof rawOccurrenceDate === 'string' && rawOccurrenceDate.trim()) {
    return rawOccurrenceDate;
  }

  if (event.start?.date) {
    return dayjs(event.start.date).toISOString();
  }

  return undefined;
};

export const isAppleCalendarDesktopSupported = (): boolean => {
  const desktopBridge = getDesktopBridge();
  return (
    desktopBridge?.platform === 'darwin' && typeof desktopBridge?.getAppleCalendars === 'function'
  );
};

export const getAppleCalendarAllowsModificationsDesktop = (
  calendar?: Pick<Calendar, 'rawJson'> & {
    allowsModifications?: boolean;
    allowsContentModifications?: boolean;
  }
): boolean | undefined => {
  if (!calendar) {
    return undefined;
  }

  if (typeof calendar.allowsModifications === 'boolean') {
    return calendar.allowsModifications;
  }

  if (typeof calendar.allowsContentModifications === 'boolean') {
    return calendar.allowsContentModifications;
  }

  const rawJson = parseAppleRawJson(calendar.rawJson);
  const nestedCalendar =
    rawJson && typeof rawJson.calendar === 'object'
      ? (rawJson.calendar as Record<string, unknown>)
      : undefined;

  const directAllows =
    getBooleanField(rawJson, 'allowsModifications') ??
    getBooleanField(rawJson, 'allowsContentModifications');

  if (typeof directAllows === 'boolean') {
    return directAllows;
  }

  return (
    getBooleanField(nestedCalendar, 'allowsModifications') ??
    getBooleanField(nestedCalendar, 'allowsContentModifications')
  );
};

export const isReadOnlyAppleBirthdayCalendarDesktop = (
  calendar?: Pick<Calendar, 'calendarType' | 'name' | 'rawJson'> & {
    allowsModifications?: boolean;
    allowsContentModifications?: boolean;
  }
): boolean => {
  if (!isAppleCalendarDesktopSupported()) {
    return false;
  }

  if (calendar?.calendarType !== 'apple') {
    return false;
  }

  const calendarName = calendar.name?.toLowerCase?.() ?? '';
  if (!calendarName.includes('birthday')) {
    return false;
  }

  // Match mobile behavior: birthday calendars are treated as read-only unless
  // we can explicitly confirm allowsModifications === true.
  return getAppleCalendarAllowsModificationsDesktop(calendar) !== true;
};

export const getAppleCalendarSyncWindow = (
  syncedDates: Dayjs[] = []
): AppleCalendarBridgeSyncWindow => {
  if (syncedDates.length) {
    return {
      startTime: syncedDates[0].startOf('day').toISOString(),
      endTime: syncedDates[syncedDates.length - 1].endOf('day').toISOString(),
    };
  }

  return {
    startTime: dayjs().subtract(LOOK_BEHIND_DAYS, 'day').startOf('day').toISOString(),
    endTime: dayjs().add(LOOK_AHEAD_DAYS, 'day').endOf('day').toISOString(),
  };
};

export const fetchAppleCalendarsFromDesktop = async ({
  startTime,
  endTime,
}: AppleCalendarBridgeSyncWindow): Promise<AppleCalendar[]> => {
  try {
    const getAppleCalendars = ensureBridgeMethod('getAppleCalendars');
    const response = await getAppleCalendars({
      startDate: startTime,
      endDate: endTime,
    });

    if (!response?.ok) {
      const errorMessage = response?.error || "Couldn't load calendars from the desktop app.";
      const statusSuffix = response?.authorizationStatus
        ? ` (status: ${response.authorizationStatus})`
        : '';
      throw new Error(`${errorMessage}${statusSuffix}`);
    }

    return response.appleCalendars ?? [];
  } catch (error) {
    throw normalizeAppleBridgeError(error);
  }
};

export const saveEventToAppleCalendarDesktop = async (
  event: CreateAppleDesktopEventData,
  calendarIdentifier?: string
): Promise<NativeAppleEvent | null> => {
  try {
    const createAppleCalendarEvent = ensureBridgeMethod('createAppleCalendarEvent');
    const recurrenceRule = recurrenceStringToAppleRecurrenceRuleDesktop(event.recurrenceString);

    const response = await createAppleCalendarEvent({
      title: event.title,
      startDate: event.startDate,
      endDate: event.endDate,
      allDay: event.isAllDay || false,
      description: event.description,
      location: event.location,
      calendarIdentifier,
      ...(recurrenceRule ? { recurrenceRule } : {}),
    });

    if (!response?.ok || !response?.event) {
      throw new Error(response?.error || "Couldn't create that event in Apple Calendar.");
    }

    return response.event;
  } catch (error) {
    throw normalizeAppleBridgeError(error);
  }
};

interface UpdateAppleDesktopEventOptions {
  calendarIdentifier?: string;
  occurrenceDate?: string;
  originalOccurrenceDate?: string;
  futureEvents?: boolean;
  recurrenceExplicitlySet?: boolean;
}

export const updateEventInAppleCalendarDesktop = async (
  eventId: string,
  event: CreateAppleDesktopEventData,
  options: UpdateAppleDesktopEventOptions = {}
): Promise<{ appleEvent: NativeAppleEvent | null; success: boolean }> => {
  try {
    const updateAppleCalendarEvent = ensureBridgeMethod('updateAppleCalendarEvent');
    const recurrenceRule = recurrenceStringToAppleRecurrenceRuleDesktop(event.recurrenceString);

    const payload: DesktopUpdateAppleEventPayload = {
      eventId,
      title: event.title,
      startDate: event.startDate,
      endDate: event.endDate,
      allDay: event.isAllDay || false,
      description: event.description,
      location: event.location,
      calendarIdentifier: options.calendarIdentifier,
      occurrenceDate: options.occurrenceDate,
      originalOccurrenceDate: options.originalOccurrenceDate,
      futureEvents: options.futureEvents,
    };

    if (options.recurrenceExplicitlySet) {
      payload.recurrenceRule = recurrenceRule;
      payload.clearRecurrence = !recurrenceRule;
    }

    const response = await updateAppleCalendarEvent(payload);

    if (!response?.ok) {
      throw new Error(response?.error || "Couldn't update that event in Apple Calendar.");
    }

    return {
      appleEvent: response.event || null,
      success: !!response.event,
    };
  } catch (error) {
    throw normalizeAppleBridgeError(error);
  }
};

export const updateSingleOccurrenceInAppleCalendarDesktop = async (
  seriesId: string,
  event: CreateAppleDesktopEventData,
  occurrenceDate: string,
  calendarIdentifier?: string
): Promise<{ appleEvent: NativeAppleEvent | null; success: boolean }> => {
  try {
    const replacementEvent = await saveEventToAppleCalendarDesktop(event, calendarIdentifier);

    if (!replacementEvent) {
      return { appleEvent: null, success: false };
    }

    try {
      const deleted = await deleteRecurringEventFromAppleCalendarDesktop(
        seriesId,
        'thisEvent',
        occurrenceDate
      );

      if (!deleted) {
        throw new Error("Couldn't delete that recurring event.");
      }
    } catch {
      try {
        await deleteRecurringEventFromAppleCalendarDesktop(replacementEvent.id, 'allEvents');
      } catch {
        // Best-effort rollback only.
      }

      return { appleEvent: null, success: false };
    }

    return { appleEvent: replacementEvent, success: true };
  } catch (error) {
    throw normalizeAppleBridgeError(error);
  }
};

export const deleteRecurringEventFromAppleCalendarDesktop = async (
  eventId: string,
  span: RecurringEventSpan = 'allEvents',
  occurrenceDate?: string
): Promise<boolean> => {
  try {
    const deleteAppleCalendarEvent = ensureBridgeMethod('deleteAppleCalendarEvent');
    const response = await deleteAppleCalendarEvent({
      eventId,
      occurrenceDate: span === 'thisEvent' ? occurrenceDate : undefined,
      futureEvents: span === 'allEvents',
    });

    if (!response?.ok) {
      throw new Error(response?.error || "Couldn't delete that event in Apple Calendar.");
    }

    return !!response.deleted;
  } catch (error) {
    throw normalizeAppleBridgeError(error);
  }
};

export const openAppleCalendarSettings = async (): Promise<boolean> => {
  const desktopBridge = getDesktopBridge();
  if (typeof desktopBridge?.openAppleCalendarSettings !== 'function') {
    return false;
  }

  try {
    return await desktopBridge.openAppleCalendarSettings();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (isBridgeHandlerMissing(message)) {
      return false;
    }
    throw normalizeAppleBridgeError(error);
  }
};
